import Autocomplete from '@mui/material/Autocomplete';
import { useTranslation } from 'react-i18next';
import { ListType, SortedCategoryCodes } from './utils';
import { Controller, Control, useFormContext } from 'react-hook-form';
import { HNMerchantCategory, MerchantSpendRule } from 'API';
import { Box, Typography, FormControl, Select, MenuItem, TextField } from '@mui/material';

// Define the types for the props
interface SpendingByCategoryProps {
  control: Control<any>;
  selectedSpendRule?: MerchantSpendRule;
  isDisabled?: boolean;
}

const SpendingByCategory = ({
  control,
  selectedSpendRule,
  isDisabled,
}: SpendingByCategoryProps): JSX.Element => {
  const { t } = useTranslation();

  const { setValue, watch } = useFormContext();
  const typeValue = watch('spendingByCategoryType');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: '16px',
        backgroundColor: (theme) => theme.palette.primary.lighter,
        borderRadius: '16px',
      }}
    >
      <Typography variant="h4" color="primary.main" mb={0.5}>
        {t('spendRules.spendingByCategory')}
      </Typography>
      <Typography variant="small" mb={1}>
        {t('byMerchantCategoryCodes')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: '24px',
          backgroundColor: '#fff',
          borderRadius: '16px',
        }}
      >
        <Controller
          name="spendingByCategoryType"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth>
              <Select
                {...field}
                onChange={(e) => {
                  const val = e.target.value as ListType;
                  field.onChange(val);
                  if (val === ListType.NO_RESTRICTIONS) {
                    setValue('spendingByCategory', []);
                  }
                }}
                variant="standard"
                disableUnderline
                disabled={isDisabled}
              >
                <MenuItem value={ListType.NO_RESTRICTIONS}>{t('spendRules.noRestrictions')}</MenuItem>
                <MenuItem value={ListType.BLOCK_LIST}>{t('spendRules.allCategoriesExcept')}</MenuItem>
                <MenuItem value={ListType.ALLOW_LIST}>{t('spendRules.noCategoriesExcept')}</MenuItem>
              </Select>
            </FormControl>
          )}
        />

        {typeValue !== ListType.NO_RESTRICTIONS && (
          <Box mt={1}>
            <Controller
              name="spendingByCategory"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  options={Object.values(HNMerchantCategory).sort()}
                  getOptionLabel={(code) => SortedCategoryCodes[code] || code}
                  filterSelectedOptions
                  disabled={isDisabled}
                  value={field.value ?? []}
                  onChange={(_, value) => field.onChange(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      InputProps={{ ...params.InputProps, disableUnderline: true }}
                    />
                  )}
                />
              )}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SpendingByCategory;
